// @flow
import { useTranslation, type TFunction } from 'react-i18next';
import { useQuery, type QueryResult } from 'react-query';
import { useToast, ToastTypes } from 'common/components/core/toast';
import usePersonnel from 'common/hooks/usePersonnel';
import useClientId from 'common/hooks/useClientId';
import { productSettings } from 'platform/api';
import { type Product, ProductIdsMap } from 'platform/settings/utils';
import useVisibleProductSettingSections from 'platform/settings/useVisibleProductSettingSections';

import toProductSettingsList from './utils';
import type { ProductSettingsList } from './utils';

const fetchProductSettings = async (
    products: Array<Product>,
    clientId: string,
    t: TFunction,
    isGlobalPersonnel: boolean
) => {
    // extracted only 'metadata' from settings api for all products.
    const data = await Promise.all(
        products.map(async product => {
            const {
                presentationMetaData: metadata
            } = await productSettings.fetch({
                clientId,
                product: ProductIdsMap[product]
            });

            return metadata;
        })
    );

    // flattened array of settings for all eligible products.
    let productSettingsList = [];

    // aggregating the data into a single array and extract only the needed info.
    for (let i = 0; i < data.length; i++) {
        productSettingsList = productSettingsList.concat(
            toProductSettingsList(data[i], [products[i]], isGlobalPersonnel, t)
        );
    }

    return productSettingsList;
};

const useProductSettingsList = (
    enabled: boolean
): QueryResult<ProductSettingsList> => {
    const { t } = useTranslation('platform');

    const { showToast } = useToast();
    const clientId = useClientId();
    const productsToFetch = useVisibleProductSettingSections();
    const isGlobalPersonnel = usePersonnel().isGlobalPersonnel();

    return useQuery({
        queryKey: ['product', productsToFetch, 'settings'],
        queryFn: () =>
            fetchProductSettings(
                productsToFetch,
                clientId,
                t,
                isGlobalPersonnel
            ),
        onError: () => {
            showToast({
                message: t('settings.messages.errorMessage'),
                type: ToastTypes.Error
            });
        },
        refetchOnWindowFocus: false,
        staleTime: Infinity,
        enabled
    });
};

export default useProductSettingsList;
